/**
 * supernovaChainError — the typed error every Supernova chain path throws, and
 * the code list that goes with it.
 *
 * It lives in its own module for one reason: supernovaChain.js imports viem, and
 * the modules that only need to *describe* a refusal (supernovaSignatureRequest)
 * must not drag a wallet library in behind them — nor form an import cycle with
 * the service that re-exports them. supernovaChain.js re-exports both names, so
 * every existing importer is unaffected and `instanceof` still holds: there is
 * exactly one class here, not a copy per module.
 */

export const SUPERNOVA_ERROR = {
  INVALID_WALLET: "INVALID_WALLET",
  NO_CLIENT: "NO_CLIENT",
  WRONG_CHAIN: "WRONG_CHAIN",
  CHAIN_READ_FAILED: "CHAIN_READ_FAILED",
  CHAIN_READ_MALFORMED: "CHAIN_READ_MALFORMED",
  STAKE_READ_UNAVAILABLE: "STAKE_READ_UNAVAILABLE",
  STAKE_DECODE_FAILED: "STAKE_DECODE_FAILED",
  NOT_IMPLEMENTED: "NOT_IMPLEMENTED",
  BUY_DISABLED: "BUY_DISABLED",
  /* The member saw the signature request and said no. Not a failure — a
     decision, and the only write outcome that is fully recoverable by just
     asking again. Kept distinct from WRITE_FAILED so the UI never tells someone
     who cancelled that something "went wrong". */
  USER_REJECTED: "USER_REJECTED",
  WRITE_FAILED: "WRITE_FAILED",
  /* A hardcoded safety assertion failed on the way to a signature prompt: the
     transaction's target wasn't the contract we hold as a constant, the approval
     operator wasn't the staking contract, or the calldata didn't match the modal
     state that produced it. It means OUR code is wrong, not the member's input,
     so it is deliberately distinct from WRITE_FAILED — and it always fires
     BEFORE the wallet is asked for anything. */
  GUARD_FAILED: "GUARD_FAILED",
};

/**
 * Typed error. `message` is member-safe copy — the UI may render it directly.
 * The underlying error hides in `cause` for the console, never on screen.
 */
export class SupernovaChainError extends Error {
  constructor(code, message, cause) {
    super(message);
    this.name = "SupernovaChainError";
    this.code = code;
    this.cause = cause;
    /* True when the feature simply isn't wired up yet (a placeholder state),
       as opposed to something that went wrong (an error state). */
    this.unavailable =
      code === SUPERNOVA_ERROR.STAKE_READ_UNAVAILABLE ||
      code === SUPERNOVA_ERROR.NOT_IMPLEMENTED ||
      code === SUPERNOVA_ERROR.BUY_DISABLED;
    /* True when the member declined the signature. The caller recovers by
       putting the form back exactly as it was — nothing was submitted. */
    this.rejected = code === SUPERNOVA_ERROR.USER_REJECTED;
  }
}
